import { useEffect, useId, useState } from 'react';

import { Button, Icon } from '@ds/core';
import { FormField, Textarea } from '@ds/forms';
import { enviarAoContato } from '@/comuns/mensagens';
import { SeletorDeCanal } from '@/comuns/SeletorDeCanal';
import { useAviso } from '@/comuns/toast';
import { dados } from '@/data/porta';

export interface NotificarPacienteProps {
    contatoId: string;
    nome: string;
    /** Texto sugerido, com a mudança escrita. A pessoa pode reescrever. */
    mensagem: string;
    onFeito: () => void;
    onDepois: () => void;
}

/**
 * Avisar o paciente de uma mudança no agendamento.
 *
 * "Notificar" mostrava "Paciente notificado" e não mandava nada. Agora o
 * WhatsApp sai pela conversa de verdade, "Ligar" é o telefone do cadastro, e o
 * e-mail — que a Fórmula MED ainda não envia — aparece desligado, com o motivo.
 * Continua sem avisar sozinho: numa reorganização de agenda, quem comunica é uma
 * pessoa.
 */
export function NotificarPaciente({ contatoId, nome, mensagem, onFeito, onDepois }: NotificarPacienteProps) {
    const aviso = useAviso();
    const idTexto = useId();
    const [canalId, setCanalId] = useState('');
    const [texto, setTexto] = useState(mensagem);
    const [enviando, setEnviando] = useState(false);
    /* `undefined` é "lendo"; `null`, "sem telefone no cadastro". */
    const [telefone, setTelefone] = useState<string | null | undefined>(undefined);
    const [erroDoTelefone, setErroDoTelefone] = useState<string | null>(null);

    useEffect(() => { setTexto(mensagem); }, [mensagem]);

    useEffect(() => {
        let vivo = true;
        setTelefone(undefined);
        setErroDoTelefone(null);
        dados().contatoPorId(contatoId)
            .then((c) => { if (vivo) setTelefone(c?.telefone || null); })
            .catch((e: unknown) => { if (vivo) setErroDoTelefone(e instanceof Error ? e.message : 'Não foi possível ler o telefone.'); });
        return () => { vivo = false; };
    }, [contatoId]);

    const enviar = async () => {
        setEnviando(true);
        try {
            const m = await enviarAoContato(contatoId, canalId, texto.trim());
            if (m.status === 'pendente') {
                aviso({ title: 'Mensagem registrada, ainda não saiu', description: 'Ela fica na conversa e sai quando o canal entregar.', tone: 'warning' });
            } else if (m.status === 'falhou') {
                aviso({ title: 'A mensagem não saiu', description: 'Veja a conversa na Comunicação.', tone: 'danger' });
            } else {
                aviso({ title: 'Paciente avisado pelo WhatsApp', description: nome });
            }
            onFeito();
        } catch (e) {
            aviso({ title: 'Não foi possível enviar', description: e instanceof Error ? e.message : undefined, tone: 'danger' });
        } finally {
            setEnviando(false);
        }
    };

    const digitos = (telefone ?? '').replace(/\D/g, '');

    return (
        <section
            aria-label="Avise o paciente"
            style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 14, background: 'var(--warning-soft)', border: '1px solid var(--warning)', borderRadius: 'var(--radius-md)' }}
        >
            <div>
                <p style={{ margin: 0, fontSize: 13.5, fontWeight: 600, color: 'var(--text-primary)' }}>Avise o paciente</p>
                <p style={{ margin: '3px 0 0', fontSize: 12.5, color: 'var(--text-secondary)' }}>
                    Você alterou o que {nome} precisa saber. A Fórmula MED não avisa sozinha: escolha por onde falar.
                </p>
            </div>

            <SeletorDeCanal value={canalId} onChange={setCanalId} tipos={['whatsapp']} />

            <FormField label="Mensagem" htmlFor={idTexto}>
                <Textarea id={idTexto} value={texto} onChange={setTexto} rows={3} />
            </FormField>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <Button
                    size="sm" iconLeft={<Icon name="message-circle" size={14} />}
                    disabled={!canalId || !texto.trim()} loading={enviando}
                    onClick={() => void enviar()}
                >
                    Enviar pelo WhatsApp
                </Button>
                {digitos && (
                    <a
                        href={`tel:${digitos}`}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: 32, padding: '0 12px', borderRadius: 'var(--radius-control)', border: '1px solid var(--border-strong)', background: 'var(--surface)', color: 'var(--neutral-700)', fontSize: 13, fontWeight: 600, textDecoration: 'none' }}
                    >
                        <Icon name="phone" size={14} />
                        Ligar para {telefone}
                    </a>
                )}
                {telefone === null && <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>Sem telefone no cadastro para ligar.</span>}
                {erroDoTelefone && <span style={{ fontSize: 12.5, color: 'var(--danger-strong)' }}>{erroDoTelefone}</span>}
                <Button size="sm" variant="secondary" iconLeft={<Icon name="mail" size={14} />} disabled>E-mail</Button>
                <span style={{ marginLeft: 'auto' }} />
                <Button size="sm" variant="ghost" onClick={onDepois}>Agora não</Button>
            </div>
            <p style={{ margin: 0, fontSize: 12, color: 'var(--text-tertiary)' }}>E-mail ainda não sai pela Fórmula MED.</p>
        </section>
    );
}
